import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { orpc } from "@/lib/orpc";
import { useCan, useCanOrganizationWide } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import {
  NO_PARENT,
  toCategoryRows,
  type ExpenseCategory,
} from "./-components/billing/expense-category-catalog";
import {
  ExpenseCategoryForm,
  type ExpenseCategoryFormValues,
} from "./-components/billing/expense-category-form";
import { ExpenseCategoryTable } from "./-components/billing/expense-category-table";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";

export const Route = createFileRoute("/_authenticated/_administration/settings/expense-categories")(
  {
    beforeLoad: ({ context }) => guardRoute(context.me, "expense-categories"),
    component: ExpenseCategoriesSettings,
  },
);

const fail = (error: Error) => toast.error(errorWords(error));

const SHARED =
  " Every building shares them, so only someone who manages the books for the whole organization can change them.";

type Editing = { mode: "create" } | { mode: "edit"; category: ExpenseCategory };

/** The chart of accounts the P&L is built from: what money spent gets booked to. */
function ExpenseCategoriesSettings() {
  // The chart is every building's, so only the organization's bookkeepers change it.
  const canManage = useCanOrganizationWide("financial:manage");
  const keepsBooksHere = useCan("financial:manage");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deactivating, setDeactivating] = useState<ExpenseCategory | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const categories = useQuery(orpc.financials.expenseCategories.list.queryOptions({}));
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.financials.expenseCategories.key() });

  const create = useMutation(
    orpc.financials.expenseCategories.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Category created");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.financials.expenseCategories.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Category updated");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const deactivate = useMutation(
    orpc.financials.expenseCategories.deactivate.mutationOptions({
      onSuccess: async () => {
        toast.success("Category deactivated. Entries already booked to it are untouched.");
        setDeactivating(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const all = categories.data ?? [];
  const rows = toCategoryRows(all, expanded);
  const parents = all.filter(
    (category) =>
      !category.parentId && (editing?.mode !== "edit" || category.id !== editing.category.id),
  );

  const submit = (values: ExpenseCategoryFormValues) => {
    if (editing?.mode === "edit") {
      update.mutate({ ...values, categoryId: editing.category.id });
      return;
    }
    create.mutate(values);
  };

  const toggle = (categoryId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });

  return (
    <>
      <PageHeader
        title="Expense categories"
        meta={`What money spent gets booked to. Each category is a line on your P&L.${
          keepsBooksHere && !canManage ? SHARED : ""
        }`}
      />

      <RecordCard
        title="Categories"
        count={categories.data ? all.length : undefined}
        canWrite={canManage}
        addLabel="Add category"
        onAdd={() => setEditing({ mode: "create" })}
      >
        {categories.isPending ? (
          <RowsSkeleton />
        ) : categories.isError ? (
          <ReadFailed
            message="The expense categories could not be loaded."
            error={categories.error}
            retrying={categories.isFetching}
            onRetry={() => void categories.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyRows text="No expense categories yet. Add one so staff can book spending to it, like Utilities or Food." />
        ) : (
          <ExpenseCategoryTable
            rows={rows}
            canManage={canManage}
            expanded={expanded}
            onToggle={toggle}
            onEdit={(category) => setEditing({ mode: "edit", category })}
            onDeactivate={setDeactivating}
            onReactivate={(category) => update.mutate({ categoryId: category.id, isActive: true })}
          />
        )}
      </RecordCard>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.mode === "edit" ? "Edit category" : "Add category"}</DialogTitle>
            <DialogDescription>
              Tax-efficiency items sit below operating income on the P&amp;L and leave net operating
              income alone.
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <ExpenseCategoryForm
              key={editing.mode === "edit" ? editing.category.id : "new"}
              parents={parents}
              pending={create.isPending || update.isPending}
              submitLabel={editing.mode === "edit" ? "Save category" : "Add category"}
              defaultValues={
                editing.mode === "edit"
                  ? {
                      name: editing.category.name,
                      description: editing.category.description ?? "",
                      categoryType: editing.category.categoryType,
                      parentId: editing.category.parentId ?? NO_PARENT,
                      displayOrder: editing.category.displayOrder,
                    }
                  : undefined
              }
              onSubmit={submit}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deactivating !== null}
        onOpenChange={(open) => (open ? null : setDeactivating(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate &ldquo;{deactivating?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops being offered on new expense entries. Entries already booked to it, and the
              P&amp;L lines they feed, are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deactivating && deactivate.mutate({ categoryId: deactivating.id })}
            >
              Deactivate category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
